"use client";

import useEmblaCarousel, {
  type UseEmblaCarouselType,
} from "embla-carousel-react";

import { cn } from "@repo/ui/lib/utils";
import { Button } from "@repo/ui/components/ui/button";
import {
  ComponentProps,
  createContext,
  forwardRef,
  HTMLAttributes,
  /* eslint-disable-next-line no-redeclare */
  KeyboardEvent,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { DotButton, useDotButton } from "@repo/ui/components/ui/dot-button";
import { Switch } from "@repo/ui/components/ui/switch";
import Autoplay from "embla-carousel-autoplay";
import Icon from "@repo/ui/components/ui/icon/icon";

export type CarouselApi = UseEmblaCarouselType[1];
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>;
type CarouselOptions = UseCarouselParameters[0];
type CarouselPlugin = UseCarouselParameters[1];

type CarouselProps = {
  opts?: CarouselOptions;
  plugins?: CarouselPlugin;
  forceTheme?: "light" | "dark";
  orientation?: "horizontal" | "vertical";
  scrollSnapPosition?: "start" | "center" | "end";
  scrollSnapClass?: string;
  scrollSnapSlim?: boolean;
  /* eslint-disable-next-line no-unused-vars */
  setApi?: (api: CarouselApi) => void;
  autoplay?: boolean;
  thumb?: boolean;
  navigation?: boolean;
};

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0];
  api: ReturnType<typeof useEmblaCarousel>[1];
  scrollPrev: () => void;
  scrollNext: () => void;
  canScrollPrev: boolean;
  canScrollNext: boolean;
} & CarouselProps;

const CarouselContext = createContext<CarouselContextProps | null>(null);

function useCarousel() {
  const context = useContext(CarouselContext);

  if (!context) {
    throw new Error("useCarousel must be used within a <Carousel />");
  }

  return context;
}

const Carousel = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & CarouselProps
>(
  (
    {
      orientation = "horizontal",
      autoplay = true,
      thumb = false,
      navigation = false,
      scrollSnapPosition = "center",
      forceTheme,
      scrollSnapClass,
      scrollSnapSlim,
      opts,
      setApi,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const plugins = autoplay ? [Autoplay({ delay: 3000 })] : [];
    const [carouselRef, api] = useEmblaCarousel(
      {
        ...opts,
        axis: orientation === "horizontal" ? "x" : "y",
      },
      plugins,
    );
    const [canScrollPrev, setCanScrollPrev] = useState(false);
    const [canScrollNext, setCanScrollNext] = useState(false);

    const onSelect = useCallback((api: CarouselApi) => {
      if (!api) {
        return;
      }

      setCanScrollPrev(api.canScrollPrev());
      setCanScrollNext(api.canScrollNext());
    }, []);

    const scrollPrev = useCallback(() => {
      api?.scrollPrev();
    }, [api]);

    const scrollNext = useCallback(() => {
      api?.scrollNext();
    }, [api]);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          scrollPrev();
        } else if (event.key === "ArrowRight") {
          event.preventDefault();
          scrollNext();
        }
      },
      [scrollPrev, scrollNext],
    );

    const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(api);

    useEffect(() => {
      if (!api || !setApi) {
        return;
      }

      setApi(api);
    }, [api, setApi]);

    useEffect(() => {
      if (!api) {
        return;
      }

      onSelect(api);
      api.on("reInit", onSelect);
      api.on("select", onSelect);

      return () => {
        api?.off("select", onSelect);
      };
    }, [api, onSelect]);

    return (
      <CarouselContext.Provider
        value={{
          carouselRef,
          api: api,
          opts,
          orientation:
            orientation || (opts?.axis === "y" ? "vertical" : "horizontal"),
          thumb,
          scrollPrev,
          scrollNext,
          canScrollPrev,
          canScrollNext,
        }}
      >
        <div
          ref={ref}
          onKeyDownCapture={handleKeyDown}
          className={cn(
            "relative rounded-b-[60px] overflow-hidden",
            className,
            { static: orientation === "vertical" },
          )}
          role="region"
          aria-roledescription="carousel"
          {...props}
        >
          {children}

          <div
            className={cn(
              `absolute w-full bottom-[20%] flex justify-${scrollSnapPosition} items-center gap-[8px]`,
              {
                "flex-col -right-[10px] top-0 w-0 h-full":
                  orientation === "vertical",
              },
              scrollSnapClass,
            )}
          >
            {!thumb &&
              !navigation &&
              scrollSnaps.map((_, index) => (
                <DotButton
                  aria-label={`slide-${index}`}
                  key={index}
                  onClick={() => onDotButtonClick(index)}
                  className={cn(
                    "w-[20px] h-[8px] rounded-[4px] bg-primary-black/40 dark:bg-primary-white/40 max-md:h-[4px]",
                    {
                      "w-[40px] bg-primary-black dark:bg-primary-white":
                        index === selectedIndex,
                    },
                    {
                      "w-[4px] h-[20px] max-md:h-[20px]":
                        orientation === "vertical",
                    },
                    {
                      "h-[40px] max-md:h-[40px]":
                        orientation === "vertical" && index === selectedIndex,
                    },
                    {
                      "h-1": scrollSnapSlim && orientation === "horizontal",
                    },
                    {
                      "dark:bg-primary-black/40": forceTheme === "light",
                    },
                    {
                      "dark:bg-primary-black":
                        forceTheme === "light" && index === selectedIndex,
                    },
                    {
                      "bg-primary-white/40": forceTheme === "dark",
                    },
                    {
                      "bg-primary-white":
                        forceTheme === "dark" && index === selectedIndex,
                    },
                  )}
                />
              ))}
          </div>
          {api && navigation && (
            <div className="w-full flex justify-center mt-[40px]">
              <div className="flex gap-[10px] items-center">
                <CarouselPrevious className="z-20" />
                {api.selectedScrollSnap() + 1} di {api.scrollSnapList().length}
                <CarouselNext className="z-20" />
              </div>
            </div>
          )}
          {autoplay && (
            <Switch
              className="absolute right-[40px] bottom-[40px] max-md:hidden"
              aria-label="suspend"
              uncheckedIcon="Resume"
              checkedIcon="Pause"
              onCheckedChange={(checked: boolean) =>
                checked
                  ? api?.plugins()?.autoplay.stop()
                  : api?.plugins()?.autoplay.play()
              }
            ></Switch>
          )}
        </div>
      </CarouselContext.Provider>
    );
  },
);
Carousel.displayName = "Carousel";

const CarouselContent = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { carouselRef, orientation, api, thumb } = useCarousel();
  const [thumbsRef, thumbsApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
    axis: orientation === "vertical" ? "y" : "x",
  });
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!api || !thumbsApi) return;
      api.scrollTo(index);
    },
    [api, thumbsApi],
  );

  const onSelect = useCallback(() => {
    if (!api || !thumbsApi) return;
    setSelectedIndex(api.selectedScrollSnap());
    thumbsApi.scrollTo(api.selectedScrollSnap());
  }, [api, thumbsApi, setSelectedIndex]);

  useEffect(() => {
    if (!api) return;
    onSelect();

    api.on("select", onSelect).on("reInit", onSelect);
  }, [api, onSelect]);

  return (
    <div
      className={cn({
        "flex flex-row-reverse": thumb && orientation === "vertical",
      })}
    >
      <div
        ref={carouselRef}
        className={cn("overflow-hidden", {
          "rounded-[20px] shadow-toggle dark:shadow-none m-[8px] dark:bg-primary-white":
            thumb,
          "flex-1": thumb && orientation === "vertical",
        })}
      >
        <div
          ref={ref}
          className={cn(
            "flex",
            orientation === "horizontal" ? "-ml-4" : "-mt-4 flex-col",
            className,
          )}
          {...props}
        />
      </div>
      {thumb && (
        <div
          className={cn("relative mt-[20px]", {
            "mt-0": orientation === "vertical",
          })}
        >
          <div
            className={cn(
              "absolute w-full h-full flex justify-between items-center pr-[18px]",
              { "flex-col pr-[10px]": orientation === "vertical" },
            )}
          >
            <CarouselPrevious
              className={cn("z-20", {
                "rotate-90": orientation === "vertical",
              })}
            />
            <CarouselNext
              className={cn("z-20", {
                "rotate-90": orientation === "vertical",
              })}
            />
          </div>
          <div
            ref={thumbsRef}
            className={cn(
              "pl-[50px] overflow-hidden z-0 py-[5px] pr-[5px] mr-[8px]",
              { "px-[5px] py-0 h-[680px]": orientation === "vertical" },
            )}
          >
            <div
              className={cn("flex gap-[16px] flex-row", {
                "flex-col h-full": orientation === "vertical",
              })}
            >
              {props &&
                props.children &&
                (props?.children as Array<ReactElement>).map((el, index) => (
                  <div
                    className={cn(
                      "cursor-pointer min-w-[250px] h-[150px] rounded-[20px] bg-center bg-contain bg-no-repeat shadow-toggle dark:shadow-none dark:border-2 dark:border-secondary-black",
                      { "opacity-25": index === selectedIndex },
                      {
                        "dark:bg-primary-white shrink-0 min-w-[150px] h-[200px]":
                          orientation === "vertical",
                      },
                    )}
                    key={index}
                    onClick={() => onThumbClick(index)}
                    style={el.props.style}
                  />
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
});
CarouselContent.displayName = "CarouselContent";

const CarouselItem = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const { orientation } = useCarousel();

    return (
      <div
        ref={ref}
        role="group"
        aria-roledescription="slide"
        className={cn(
          "min-w-0 shrink-0 grow-0 basis-full",
          orientation === "horizontal" ? "pl-4" : "pt-4 min-h-0",
          className,
        )}
        {...props}
      />
    );
  },
);
CarouselItem.displayName = "CarouselItem";

const CarouselPrevious = forwardRef<
  HTMLButtonElement,
  ComponentProps<typeof Button>
>(({ className, variant = "ghost", size = "icon", ...props }, ref) => {
  const { scrollPrev, canScrollPrev } = useCarousel();

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        "h-8 w-8 rounded-full bg-secondary-black dark:bg-primary-white flex items-center justify-center",
        className,
      )}
      disabled={!canScrollPrev}
      onClick={scrollPrev}
      {...props}
    >
      <Icon
        icon="Arrow"
        className="h-3 w-3 stroke-primary-white dark:stroke-primary-black rotate-180"
      />
      <span className="sr-only">Previous slide</span>
    </Button>
  );
});
CarouselPrevious.displayName = "CarouselPrevious";

const CarouselNext = forwardRef<
  HTMLButtonElement,
  ComponentProps<typeof Button>
>(({ className, variant = "ghost", size = "icon", ...props }, ref) => {
  const { scrollNext, canScrollNext } = useCarousel();

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      className={cn(
        "h-8 w-8 rounded-full bg-secondary-black dark:bg-primary-white flex items-center justify-center",
        className,
      )}
      disabled={!canScrollNext}
      onClick={scrollNext}
      {...props}
    >
      <Icon
        icon="Arrow"
        className="h-3 w-3 stroke-primary-white dark:stroke-primary-black"
      />
      <span className="sr-only">Next slide</span>
    </Button>
  );
});
CarouselNext.displayName = "CarouselNext";

export {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
};
